/**
 * Environment variable contract. One table per deployable; `loadEnv` validates and types it.
 * The tables also generate `.env.example` and the env section of Docs/rebuild/30-contracts.md.
 *
 * Rules: names are frozen after T0. Secrets are marked and never get a default. Timing limits that
 * protect the E-Hentai account (RATE_LIMITS) are constants in code, not environment.
 */

import { VIDEO_MAX_BYTES } from "./contract/primitives";

type Kind = "string" | "int" | "url" | "bool" | "enum";

export interface VarSpec<V = unknown, Optional extends boolean = boolean> {
  readonly kind: Kind;
  readonly description: string;
  /** true = loadEnv fails when the variable is missing (no default, not optional) */
  readonly required: boolean;
  readonly secret: boolean;
  readonly default?: string;
  readonly values?: readonly string[];
  readonly min?: number;
  readonly minLength?: number;
  /** phantom fields: the parsed type, and whether the value may be undefined */
  readonly _v?: V;
  readonly _optional?: Optional;
}

type Opts = { default?: string; optional?: boolean; secret?: boolean; min?: number; minLength?: number };
type IsOptional<O> = O extends { optional: true } ? true : false;

function make<V, O extends Opts | undefined>(
  kind: Kind,
  description: string,
  o: O,
  values?: readonly string[],
): VarSpec<V, IsOptional<O>> {
  const opts: Opts = o ?? {};
  return {
    kind,
    description,
    required: opts.default === undefined && !opts.optional,
    secret: opts.secret ?? false,
    ...(opts.default !== undefined ? { default: opts.default } : {}),
    ...(values ? { values } : {}),
    ...(opts.min !== undefined ? { min: opts.min } : {}),
    ...(opts.minLength !== undefined ? { minLength: opts.minLength } : {}),
  } as VarSpec<V, IsOptional<O>>;
}

export const v = {
  string: <const O extends Opts | undefined = undefined>(description: string, o?: O) =>
    make<string, O>("string", description, o as O),
  secret: <const O extends Omit<Opts, "secret" | "default"> | undefined = undefined>(
    description: string,
    o?: O,
  ) =>
    make<string, O>("string", description, {
      ...o,
      secret: true,
      minLength: o?.minLength ?? 32,
    } as unknown as O),
  int: <const O extends Opts | undefined = undefined>(description: string, o?: O) =>
    make<number, O>("int", description, o as O),
  url: <const O extends Opts | undefined = undefined>(description: string, o?: O) =>
    make<string, O>("url", description, o as O),
  bool: <const O extends Opts | undefined = undefined>(description: string, o?: O) =>
    make<boolean, O>("bool", description, o as O),
  enum: <const E extends readonly string[], const O extends Opts | undefined = undefined>(
    values: E,
    description: string,
    o?: O,
  ) => make<E[number], O>("enum", description, o as O, values),
};

export type EnvTable = Record<string, VarSpec>;
export type EnvOf<T extends EnvTable> = {
  [K in keyof T]: T[K] extends VarSpec<infer V, infer Opt> ? (Opt extends true ? V | undefined : V) : never;
};

export class EnvError extends Error {
  readonly problems: string[];
  constructor(service: string, problems: string[]) {
    super(`invalid environment for ${service}:\n  ${problems.join("\n  ")}`);
    this.name = "EnvError";
    this.problems = problems;
  }
}

function parseOne(name: string, spec: VarSpec, raw: string, problems: string[]): unknown {
  switch (spec.kind) {
    case "string":
      if (spec.minLength && raw.length < spec.minLength)
        problems.push(`${name}: at least ${spec.minLength} characters`);
      return raw;
    case "url":
      try {
        new URL(raw);
      } catch {
        problems.push(`${name}: not a URL`);
      }
      return raw.replace(/\/+$/, "");
    case "int": {
      const n = Number(raw);
      if (!/^-?\d+$/.test(raw) || !Number.isSafeInteger(n)) problems.push(`${name}: not an integer`);
      else if (spec.min !== undefined && n < spec.min) problems.push(`${name}: must be >= ${spec.min}`);
      return n;
    }
    case "bool":
      if (!/^(true|false|1|0)$/.test(raw)) problems.push(`${name}: expected true/false/1/0`);
      return raw === "true" || raw === "1";
    case "enum":
      if (!spec.values?.includes(raw)) problems.push(`${name}: one of ${spec.values?.join(", ")}`);
      return raw;
  }
}

/** Validates `source` (default process.env) against `table`. Throws EnvError listing every problem. */
export function loadEnv<T extends EnvTable>(
  service: string,
  table: T,
  source: Record<string, string | undefined> = process.env,
): EnvOf<T> {
  const problems: string[] = [];
  const out: Record<string, unknown> = {};
  for (const [name, spec] of Object.entries(table)) {
    const raw = source[name];
    if (raw === undefined || raw === "") {
      if (spec.default !== undefined) out[name] = parseOne(name, spec, spec.default, problems);
      else if (spec.required) problems.push(`${name}: required (${spec.description})`);
      continue;
    }
    out[name] = parseOne(name, spec, raw, problems);
  }
  if ("SHARE_BOT_APPLICATION_ID" in table) {
    const names = ["SHARE_BOT_APPLICATION_ID", "SHARE_BOT_PUBLIC_KEY", "SHARE_BOT_TOKEN"];
    const configured = names.filter((name) => out[name] !== undefined).length;
    if (configured !== 0 && configured !== 3)
      problems.push(
        "SHARE_BOT_APPLICATION_ID, SHARE_BOT_PUBLIC_KEY and SHARE_BOT_TOKEN must be set together",
      );
    if (out.SHARE_BOT_APPLICATION_ID !== undefined && !/^[0-9]+$/.test(String(out.SHARE_BOT_APPLICATION_ID)))
      problems.push("SHARE_BOT_APPLICATION_ID: expected digits");
    if (out.SHARE_BOT_PUBLIC_KEY !== undefined && !/^[0-9a-f]{64}$/i.test(String(out.SHARE_BOT_PUBLIC_KEY)))
      problems.push("SHARE_BOT_PUBLIC_KEY: expected 64 hex characters");
  }
  if (problems.length > 0) throw new EnvError(service, problems);
  return out as EnvOf<T>;
}

// ---- tables -------------------------------------------------------------------------------------

const NODE_ENV = v.enum(["development", "test", "production"], "실행 모드", { default: "development" });
const LOG_LEVEL = v.enum(["debug", "info", "warn", "error"], "로그 수준", { default: "info" });
const AWS_REGION = v.string("AWS 리전", { default: "ap-northeast-2" });

export const apiEnv = {
  NODE_ENV,
  LOG_LEVEL,
  HOST: v.string("수신 주소", { default: "0.0.0.0" }),
  PORT: v.int("수신 포트", { default: "3000", min: 1 }),
  PUBLIC_BASE_URL: v.url("사이트의 공개 오리진(예: https://volgo.live)"),
  DATABASE_URL: v.url("PostgreSQL 연결 문자열"),
  JWT_SECRET: v.secret("액세스 토큰용 HS256 키"),
  ACCESS_TOKEN_TTL_SECONDS: v.int("액세스 토큰 수명(초)", { default: "900", min: 60 }),
  REFRESH_TOKEN_TTL_DAYS: v.int("리프레시 토큰 수명(일)", { default: "30", min: 1 }),
  COOKIE_DOMAIN: v.string("쿠키의 Domain 속성. 비우면 호스트 전용", { optional: true }),
  INTERNAL_HMAC_SECRET: v.secret("/internal 요청 서명에 쓰는 공유 비밀"),
  VIDEO_MAX_BYTES: v.int("영상 파일 최대 바이트", { default: String(VIDEO_MAX_BYTES), min: 1 }),
  MEDIA_PUBLIC_URL: v.url("영상 처리 서버 공개 주소"),
  MEDIA_SHARED_SECRET: v.secret("영상 내부 요청과 목적 분리 업로드 토큰 비밀"),
  INTERNAL_HMAC_SECRET_PREVIOUS: v.secret("이전 비밀. 교체하는 동안 함께 받는다", { optional: true }),
  DISCORD_CLIENT_ID: v.string("Discord OAuth 애플리케이션 ID"),
  DISCORD_CLIENT_SECRET: v.secret("Discord OAuth 애플리케이션 비밀", { minLength: 8 }),
  DISCORD_BOT_TOKEN: v.secret("길드 멤버·역할 조회용 Discord Bot 토큰"),
  DISCORD_GUILD_ID: v.string("가입이 필요한 Discord 서버 ID(숫자)"),
  DISCORD_REQUIRED_ROLE_IDS: v.string("하나 이상 필요한 Discord 역할 ID, 쉼표로 구분. 빈 목록 불가"),
  DISCORD_REDIRECT_URI: v.url("OAuth 콜백 주소: <PUBLIC_BASE_URL>/api/auth/discord/callback"),
  DISCORD_AUTHORIZE_URL: v.url("인가 엔드포인트(테스트는 가짜 서버로 돌린다)", {
    default: "https://discord.com/oauth2/authorize",
  }),
  DISCORD_API_BASE: v.url("토큰·사용자 엔드포인트의 기준 주소(테스트는 가짜 서버로 돌린다)", {
    default: "https://discord.com/api",
  }),
  DISCORD_ADMIN_IDS: v.string(
    "Discord로 로그인하면 관리자가 되는 Discord 사용자 ID(숫자) 목록, 쉼표로 구분. 비우면 아무도 승격하지 않는다. 목록에서 빼도 강등하지 않는다",
    { optional: true },
  ),
  SHARE_BOT_APPLICATION_ID: v.string("공유 봇 앱 ID(숫자), 다른 봇 변수와 함께 설정", { optional: true }),
  SHARE_BOT_PUBLIC_KEY: v.string("공유 봇 Ed25519 공개 키(64자리 hex)", { optional: true }),
  SHARE_BOT_TOKEN: v.secret("공유 봇 토큰", { optional: true, minLength: 1 }),
  SHARE_BOT_API_BASE: v.url("공유 봇 Discord API 기준 주소", { default: "https://discord.com/api/v10" }),
  AWS_REGION,
  S3_BUCKET: v.string("갤러리와 백업을 담는 버킷"),
  CLOUDFRONT_DOMAIN: v.string("갤러리 이미지를 내보내는 호스트(예: cdn.volgo.live)"),
  CLOUDFRONT_KEY_PAIR_ID: v.string("서명 쿠키에 쓰는 CloudFront 공개 키 ID"),
  CLOUDFRONT_PRIVATE_KEY_FILE: v.string("PEM 개인 키 파일 경로(마운트한 파일. 저장소에 두지 않는다)"),
  CDN_COOKIE_TTL_SECONDS: v.int(
    "CloudFront 서명 쿠키와 그 서명 정책의 수명(초). 액세스 토큰 수명과 별개다. 로그인·토큰 갱신 때마다 새로 발급된다",
    { default: "86400", min: 60 },
  ),
  LAMBDA_ARCHIVE_DL_NAME: v.string("archive-dl 함수 이름"),
  LAMBDA_HITOMI_DL_NAME: v.string("hitomi-dl 함수 이름"),
  AWS_ENDPOINT_URL: v.url("AWS SDK 호출 주소 덮어쓰기(로컬 가짜용)", { optional: true }),
} satisfies EnvTable;

export const workerEnv = {
  NODE_ENV,
  LOG_LEVEL,
  DATABASE_URL: v.url("PostgreSQL 연결 문자열"),
  AWS_REGION,
  LAMBDA_ARCHIVE_DL_NAME: v.string("archive-dl 함수 이름"),
  S3_BUCKET: v.string("갤러리와 백업을 담는 버킷"),
  AWS_ENDPOINT_URL: v.url("AWS SDK 호출 주소 덮어쓰기(로컬 가짜용)", { optional: true }),
  DISCORD_WEBHOOK_URL: v.url("알림 웹훅. 없으면 알림은 로그에만 남는다", { optional: true }),
  PG_DUMP_PATH: v.string("일간 백업에 쓰는 pg_dump 실행 파일", { default: "pg_dump" }),
  BACKUP_DAILY_KEEP: v.int("보관할 일간 백업 수", { default: "14", min: 1 }),
  BACKUP_WEEKLY_KEEP: v.int("보관할 주간 백업 수", { default: "8", min: 1 }),
  WORKER_TICK_MS: v.int("스케줄러 틱 간격(ms)", { default: "1000", min: 10 }),
} satisfies EnvTable;

const lambdaCommon = {
  NODE_ENV,
  LOG_LEVEL,
  AWS_REGION,
  INTERNAL_API_BASE_URL: v.url("API의 오리진(예: https://volgo.live)"),
  INTERNAL_HMAC_SSM_PARAM: v.string("HMAC 비밀을 담은 SSM SecureString", { optional: true }),
  INTERNAL_HMAC_SECRET: v.secret("직접 주는 HMAC 비밀(테스트·로컬 실행용)", { optional: true }),
  EH_COOKIES_SSM_PARAM: v.string("ExHentai 쿠키 헤더를 담은 SSM SecureString", { optional: true }),
  EH_COOKIES: v.secret("직접 주는 쿠키 헤더(테스트·로컬 실행용)", { optional: true, minLength: 1 }),
  EH_SITE_URL: v.url("E-Hentai 오리진(테스트는 가짜 서버로 돌린다)", { default: "https://e-hentai.org" }),
  EXH_SITE_URL: v.url("ExHentai 오리진(테스트는 가짜 서버로 돌린다)", { default: "https://exhentai.org" }),
  EH_API_URL: v.url("gdata 엔드포인트(테스트는 가짜 서버로 돌린다)", {
    default: "https://api.e-hentai.org/api.php",
  }),
} satisfies EnvTable;

export const ehSyncEnv = { ...lambdaCommon } satisfies EnvTable;

export const archiveDlEnv = {
  ...lambdaCommon,
  S3_BUCKET: v.string("갤러리를 담는 버킷"),
  AWS_ENDPOINT_URL: v.url("AWS SDK 호출 주소 덮어쓰기(로컬 가짜용)", { optional: true }),
  ARCHIVE_MAX_BYTES: v.int("이보다 큰 아카이브는 거부(바이트)", { default: String(2 * 1024 ** 3), min: 1 }),
  TMP_DIR: v.string("임시 작업 디렉터리", { default: "/tmp" }),
  ARCHIVE_EC2_ROUTING: v.bool("true = 큰 Archive를 구매 전에 EC2 실행기로 넘긴다", { default: "false" }),
} satisfies EnvTable;

/** The EC2 Archive runner: same job code as the Lambda, without the time limit. */
export const archiveRunnerEnv = {
  ...archiveDlEnv,
  ARCHIVE_RUNNER_MAX_JOBS: v.int("동시 작업 상한(실제 상한은 전역 9슬롯)", { default: "9", min: 1 }),
  ARCHIVE_RUNNER_POLL_MS: v.int("작업 확인 간격(ms)", { default: "5000", min: 500 }),
  ARCHIVE_RUNNER_RESERVE_BYTES: v.int("디스크 여유로 남길 바이트", {
    default: String(2 * 1024 ** 3),
    min: 0,
  }),
} satisfies EnvTable;

export const hitomiDlEnv = {
  NODE_ENV,
  LOG_LEVEL,
  AWS_REGION,
  INTERNAL_API_BASE_URL: lambdaCommon.INTERNAL_API_BASE_URL,
  INTERNAL_HMAC_SSM_PARAM: lambdaCommon.INTERNAL_HMAC_SSM_PARAM,
  INTERNAL_HMAC_SECRET: lambdaCommon.INTERNAL_HMAC_SECRET,
  S3_BUCKET: v.string("갤러리를 담는 버킷"),
  AWS_ENDPOINT_URL: v.url("AWS SDK 호출 주소 덮어쓰기(로컬 가짜용)", { optional: true }),
  GALLERY_DL_PATH: v.string("이미지 안의 고정 버전 gallery-dl 실행 파일", {
    default: "/opt/gallery-dl/gallery-dl",
  }),
  TMP_DIR: v.string("임시 작업 디렉터리", { default: "/tmp" }),
} satisfies EnvTable;

/** Build-time variables of the SPA (Vite exposes only VITE_*). */
export const webEnv = {
  VITE_API_BASE_URL: v.string("API 오리진. 비우면 같은 오리진", { default: "" }),
} satisfies EnvTable;

/** The VM's compose `.env`: what compose itself reads, on top of the api and worker tables. */
export const composeEnv = {
  DOMAIN: v.string("Caddy가 서비스하는 공개 호스트 이름(예: volgo.live)"),
  POSTGRES_USER: v.string("데이터베이스 역할", { default: "volgo" }),
  POSTGRES_PASSWORD: v.secret("데이터베이스 비밀번호", { minLength: 16 }),
  POSTGRES_DB: v.string("데이터베이스 이름", { default: "volgo" }),
  IMAGE_REGISTRY: v.string("이미지의 레지스트리 경로", { default: "ghcr.io/0ppxnhximxr/volgo-live" }),
  IMAGE_TAG: v.string("실행할 이미지 태그", { default: "latest" }),
} satisfies EnvTable;

export const mediaEnv = {
  NODE_ENV,
  LOG_LEVEL,
  AWS_REGION,
  HOST: v.string("수신 주소", { default: "0.0.0.0" }),
  PORT: v.int("수신 포트", { default: "3000", min: 1 }),
  MEDIA_SHARED_SECRET: v.secret("영상 내부 요청과 목적 분리 업로드 토큰 비밀"),
  API_BASE_URL: v.url("서명 내부 API 주소"),
  S3_BUCKET: v.string("영상 저장 버킷"),
  ALLOWED_ORIGIN: v.url("업로드를 허용하는 웹 오리진"),
  MEDIA_CONCURRENCY: v.int("동시 영상 처리 개수", { default: "1", min: 1 }),
  MAX_JOB_SECONDS: v.int("처리 시간 제한", { default: "3600", min: 1 }),
  DATA_DIR: v.string("영상 작업 볼륨", { default: "/data" }),
  AWS_ENDPOINT_URL: v.url("로컬 가짜 AWS 주소", { optional: true }),
} satisfies EnvTable;

export const ENV_TABLES = {
  media: mediaEnv,
  api: apiEnv,
  worker: workerEnv,
  "lambda-eh-sync": ehSyncEnv,
  "lambda-archive-dl": archiveDlEnv,
  "archive-runner": archiveRunnerEnv,
  "lambda-hitomi-dl": hitomiDlEnv,
  web: webEnv,
  compose: composeEnv,
} as const satisfies Record<string, EnvTable>;
export type EnvService = keyof typeof ENV_TABLES;

/** Fixed pacing and budgets (U-12). Constants, not configuration: changing them is a contract change. */
export const RATE_LIMITS = {
  listIntervalMs: 3000,
  listMaxPagesPerRun: 100,
  gdataBatchSize: 25,
  gdataBurst: 4,
  gdataPauseMs: 2000,
  /** Shared permit outlives the 60s request timeout; producers subtract acquire round-trip time. */
  gdataPermitTtlMs: 90_000,
  /** gdata calls allowed in one meta run: 24 bursts of 4 */
  gdataMaxCallsPerRun: 96,
  /** Leave five seconds for the final report before the next one-minute invocation. */
  gdataRunBudgetMs: 55_000,
} as const;

/** Text of `.env.example`: every variable of the api, worker and compose tables, annotated. */
export function renderEnvExample(): string {
  // a variable shared by several tables (DATABASE_URL, S3_BUCKET, ...) is listed once, where it first appears
  const listed = new Set<string>();
  const section = (title: string, table: EnvTable) => {
    const lines = [`# ---- ${title} ${"-".repeat(Math.max(2, 70 - title.length))}`];
    for (const [name, spec] of Object.entries(table)) {
      if (listed.has(name)) continue;
      listed.add(name);
      const flags = [
        spec.required ? "required" : "optional",
        ...(spec.secret ? ["secret"] : []),
        ...(spec.values ? [`one of ${spec.values.join("|")}`] : []),
      ];
      lines.push(`# ${spec.description} (${flags.join(", ")})`);
      lines.push(`${spec.required || spec.secret ? "" : "# "}${name}=${spec.default ?? ""}`);
    }
    return lines.join("\n");
  };
  return `${[
    "# Copy to .env (never commit it). The VM keeps its .env outside the repository.",
    "# Generated from packages/core/src/env.ts by `bun run contracts:write`; do not edit by hand.",
    "",
    section("compose", composeEnv),
    "",
    section("api", apiEnv),
    "",
    section("worker", workerEnv),
  ].join("\n")}\n`;
}
